import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/layout/PageHeader'
import {
  BillingFilters,
  type BillingFilterState,
} from '@/components/billing/BillingFilters'
import { BillingMetrics } from '@/components/billing/BillingMetrics'
import { BillingTable } from '@/components/billing/BillingTable'
import { useBillingAggregation } from '@/hooks/useBillingAggregation'

function defaultFilters(): BillingFilterState {
  const now = new Date()
  const first = new Date(now.getFullYear(), now.getMonth(), 1)
  const pad = (n: number) => String(n).padStart(2, '0')
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  return {
    dateFrom: fmt(first),
    dateTo: fmt(now),
    companyId: '',
    inspectorId: '',
    city: '',
    outcome: 'all_terminal',
  }
}

export function BillingPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const initial = useMemo(() => defaultFilters(), [])
  const [draft, setDraft] = useState<BillingFilterState>(initial)
  const [applied, setApplied] = useState<BillingFilterState>(initial)

  const {
    rows,
    metrics,
    total,
    partial,
    isLoadingOrders,
    isLoadingBilling,
    ordersError,
  } = useBillingAggregation(applied)

  return (
    <div>
      <PageHeader
        title={t('billing.title')}
        subtitle={t('billing.subtitle')}
      />
      <div className="mb-4">
        <BillingFilters
          value={draft}
          onChange={setDraft}
          onApply={() => {
            setApplied(draft)
            void qc.invalidateQueries({ queryKey: ['billing-list'] })
          }}
          onReset={() => {
            const d = defaultFilters()
            setDraft(d)
            setApplied(d)
          }}
        />
      </div>

      {ordersError ? (
        <div className="mb-3 rounded-xl border border-[color:var(--stryzh-color-semantic-error)] bg-[color:var(--stryzh-color-order-card-timeout-bg)] px-3 py-2 text-sm text-[color:var(--stryzh-color-semantic-error)]">
          {t('billing.ordersLoadError', { msg: ordersError.message })}
        </div>
      ) : null}

      <div className="mb-4">
        <BillingMetrics
          metrics={metrics}
          total={total}
          partial={partial}
          isLoadingOrders={isLoadingOrders}
          isLoadingBilling={isLoadingBilling}
        />
      </div>

      <BillingTable rows={rows} isLoadingOrders={isLoadingOrders} />
    </div>
  )
}
